<template>
  <base-drawer :show="show" title="Review future slots" @close="emit('close')">
    <info-hint variant="primary" icon="icon-alert-circle-exc" class="mb-3">
      Existing slots stay unchanged unless selected below. Booked and requested slots are never removed.
    </info-hint>
    <info-hint v-if="insufficientResources" variant="warning" icon="icon-alert-circle-exc" class="mb-3">
      Existing booking slots have resources with automatic blocking enabled. New concurrent slots may be blocked automatically if resource coverage is insufficient.
    </info-hint>

    <drawer-section
      v-for="row in rows"
      :key="row.status"
      :title="row.label + ' (' + row.count + ')'"
      :accent="row.count > 0 ? row.color : null"
      collapsible
      :expanded="expanded[row.status] !== false"
      @update:expanded="val => setExpanded(row.status, val)"
    >
      <div v-if="row.removable" class="toggle-row">
        <el-switch active-color="#e14eca" v-model="row.remove" :disabled="row.count <= 0" />
        <label class="mb-0 ml-2 text-white">Remove existing slots</label>
      </div>
      <div class="toggle-row" :class="{ 'mt-1': row.removable }">
        <el-switch active-color="#e14eca" v-model="row.create_overlap" :disabled="row.count <= 0" />
        <label class="mb-0 ml-2 text-white">Create overlapping slots</label>
      </div>
      <segmented-toggle v-if="row.create_overlap" v-model="row.skip_mode" :options="SKIP_MODE_OPTIONS" variant="pills" size="sm" class="mt-2" />
    </drawer-section>

    <template #footer>
      <div class="footer-actions">
        <base-button type="secondary" size="sm" @click="emit('close')">Cancel</base-button>
        <base-button type="primary" size="sm" :disabled="loading" @click="emit('confirm')">{{ confirmLabel }}</base-button>
      </div>
    </template>
  </base-drawer>
</template>

<script lang="ts">
export default { name: 'ReviewFutureSlotsDrawer' }
</script>

<script setup lang="ts">
import { ref, watch, onBeforeUnmount } from 'vue'
import { SKIP_MODE_OPTIONS } from '@/util/scheduleFutureSlots'

interface FutureSlotRow {
  status: string
  label: string
  removable: boolean
  color: string
  count: number
  remove: boolean
  create_overlap: boolean
  skip_mode: string
}

const props = defineProps({
  show: { type: Boolean, default: false },
  rows: { type: Array as () => FutureSlotRow[], default: () => [] },
  confirmLabel: { type: String, default: 'Confirm' },
  insufficientResources: { type: Boolean, default: false },
  loading: { type: Boolean, default: false }
})
const emit = defineEmits(['close', 'confirm'])

// The parent owns the rows; toggles mutate them in place.

// Sections with no slots start collapsed; the operator can still open them.
const expanded = ref<Record<string, boolean>>({})
const resetExpanded = () => {
  const next: Record<string, boolean> = {}
  props.rows.forEach(r => { next[r.status] = r.count > 0 })
  expanded.value = next
}
const setExpanded = (status: string, val: boolean) => {
  expanded.value = { ...expanded.value, [status]: val }
}
watch(() => props.rows, resetExpanded, { immediate: true })
const onEscKey = (e: KeyboardEvent) => {
  if (e.key === 'Escape' && props.show) emit('close')
}
watch(() => props.show, (val) => {
  if (val) document.addEventListener('keydown', onEscKey)
  else document.removeEventListener('keydown', onEscKey)
})
onBeforeUnmount(() => document.removeEventListener('keydown', onEscKey))
</script>
